/*
 * terms-agreement-gate.js
 * ───────────────────────
 * Behaviour for the required Terms & Conditions / "not a convicted felon"
 * agreement checkbox rendered by snippets/terms-agreement-gate.liquid on
 * restricted products (ballistic helmet, etc.).
 *
 * Two jobs:
 *   1. Mirror the value the add-to-cart gate reads. A native checkbox's `value`
 *      attribute does NOT change when it is (un)checked, but the AGB gate in
 *      main-product-agb-system.liquid blocks the add until
 *      value === '✓' (isTermsCheckboxChecked). So we flip value between
 *      '✓' (checked) and '' (unchecked) on every change — the same trick
 *      the Bold Product Options app uses on armor products.
 *   2. Paint the red "This option is required." state the moment a shopper
 *      tries to add to cart while unchecked, and clear it once they check.
 *
 * Self-contained + fail-safe: no-ops on any page without the checkbox, never
 * touches the AGB engine, reads state only through the DOM. Isolation by
 * design — nothing here is imported by the gear builder.
 */
(function () {
  'use strict';
  var CHECKED_VALUE = '✓'; // '✓' — exact value isTermsCheckboxChecked() compares against
  var ADD_BUTTON_IDS = ['add-all-btn', 'add-all-btn-2']; // section's desktop + mobile add buttons

  function init() {
    var checkbox = document.querySelector('[data-terms-checkbox]');
    if (!checkbox) return; // not a restricted product — nothing to do

    var wrap = checkbox.closest('[data-terms-agreement]');
    var requiredMsg = wrap ? wrap.querySelector('[data-terms-required]') : null;
    // Plate-conditional gate (gear-builder plates): the snippet renders the gate
    // HIDDEN with this flag; it only applies while a plate (body armor) is in the
    // loadout. Restricted products (helmet/tagged) lack the flag → always-on.
    var plateConditional = !!(wrap && wrap.hasAttribute('data-terms-plate-conditional'));

    function syncValue() {
      checkbox.value = checkbox.checked ? CHECKED_VALUE : '';
      if (checkbox.checked) clearInvalid();
    }
    function markInvalid() {
      if (wrap) wrap.classList.add('terms-agreement--invalid');
      if (requiredMsg) requiredMsg.hidden = false;
    }
    function clearInvalid() {
      if (wrap) wrap.classList.remove('terms-agreement--invalid');
      if (requiredMsg) requiredMsg.hidden = true;
    }

    // AUTHORITATIVE plate set = the products in /collections/armor-plates, the
    // collection the merchant curates (Dorian: "those are the plates that should
    // all trigger it"). Fetched ONCE (public JSON, no auth); re-syncs the gate
    // when it lands. This is what caught the plates the handle pattern missed —
    // a soft armor insert (…soft-armor-insert) and the flank-side ceramic-plates
    // bundle (…ceramic-plates), neither of which contains "armor-plate", on a
    // BUNDLE (k-zero-sf-bundle-1) that has no panel config so AGB_isPlate/panel
    // isPlate never fired for them. Falls back to the handle pattern until the
    // fetch lands / if it fails.
    var _plateHandleSet = null; // Set once started; null = not yet requested
    function _ensurePlateCollection(onReady) {
      if (_plateHandleSet) return;              // already loading/loaded
      _plateHandleSet = new Set();               // mark started (empty = pattern still covers)
      try {
        fetch('/collections/armor-plates/products.json?limit=250', { credentials: 'omit' })
          .then(function (r) { return (r && r.ok) ? r.json() : null; })
          .then(function (d) {
            if (d && Array.isArray(d.products)) {
              d.products.forEach(function (p) { if (p && p.handle) _plateHandleSet.add(String(p.handle).toLowerCase()); });
            }
            if (typeof onReady === 'function') { try { onReady(); } catch (e) {} }
          })
          .catch(function () {});
      } catch (e) {}
    }
    // Handle-name fallback — broadened to match EVERY body-armor handle in that
    // collection: …armor-plate(s)…, …ceramic-plate(s)…, …soft-armor(-insert)…,
    // …armor-insert…. Still does NOT match a bare "…plate-carriers" (not armor).
    function isArmorPlateHandle(h) {
      return typeof h === 'string' && /armor[-\s]?plate|ceramic[-\s]?plate|soft[-\s]?armor|armor[-\s]?insert/i.test(h);
    }
    // One loadout item = body armor (a plate)? Belt-and-suspenders for a
    // compliance gate — ANY signal counts: engine isPlate, panel isPlate, the
    // armor-plates collection, or the handle name. A false positive is a harmless
    // extra checkbox; a false negative is a compliance miss.
    function isPlateItem(it) {
      if (!it || !it.handle) return false;
      var h = it.handle;
      if (typeof window.AGB_isPlate === 'function' && window.AGB_isPlate(h) === true) return true;
      var cfg = window.__AGB_GEAR_SUGG;
      var ph = window.AGB_BOOT && window.AGB_BOOT.productHandle;
      if (cfg && typeof cfg.accessoryFor === 'function') {
        try { var acc = cfg.accessoryFor(h, ph); if (acc && acc.isPlate === true) return true; } catch (e) {}
      }
      if (_plateHandleSet && _plateHandleSet.has(String(h).toLowerCase())) return true;
      return isArmorPlateHandle(h);
    }
    // Is a plate (body armor) currently in the gear-builder loadout?
    function hasPlateInLoadout() {
      var items = Array.isArray(window.acceptedItems) ? window.acceptedItems : [];
      return items.some(isPlateItem);
    }
    // Reveal the gate when a plate is added; hide + reset it when none remain so
    // it never silently blocks an add on a plate-free loadout.
    function syncPlateGate() {
      var show = hasPlateInLoadout();
      if (wrap) wrap.hidden = !show;
      if (!show) {
        if (checkbox.checked) checkbox.checked = false;
        checkbox.value = '';
        clearInvalid();
      }
    }

    // Keep the value mirror in lock-step with `checked`. The AGB gate reads
    // `value === '✓'`, NOT `.checked` — and the browser can restore a checkbox to
    // CHECKED on back/forward or reload WITHOUT firing 'change' (bfcache / form-
    // state restoration). That left the box looking checked while value stayed ''
    // → the add was blocked ("Please agree to the Terms") until the shopper
    // un/re-checked it to fire 'change' (bug reported 2026-07-13). Re-derive value
    // from checked on every signal that can change it, incl. page restoration.
    checkbox.addEventListener('change', syncValue);
    checkbox.addEventListener('input', syncValue);
    window.addEventListener('pageshow', syncValue); // bfcache / back-forward restore
    syncValue();                      // initialise from the CURRENT checked state
    requestAnimationFrame(syncValue); // catch restoration that lands right after load
    setTimeout(syncValue, 300);       // and any slightly-later form-state restore

    if (plateConditional) {
      // ⚠ The gear builder signals loadout changes on the AGB EVENT BUS
      // (window.AGB.bus.emit('acceptedItemsChanged')), NOT as a DOM event — a
      // document-only listener NEVER fires on add/remove (verified live 2026-07-13:
      // that was the bug that kept the gate hidden). Subscribe to the bus; keep the
      // DOM listener as a harmless fallback. AGB boots asynchronously (lazy Step-2
      // load) so poll briefly for the bus and attach the moment it exists.
      document.addEventListener('acceptedItemsChanged', syncPlateGate);
      // Load the authoritative armor-plates collection, then re-evaluate the gate
      // (a plate may already be in the loadout, e.g. a bundle's pre-included plate).
      _ensurePlateCollection(syncPlateGate);
      (function attachBus(tries) {
        try {
          if (window.AGB && window.AGB.bus && typeof window.AGB.bus.on === 'function') {
            window.AGB.bus.on('acceptedItemsChanged', syncPlateGate);
            syncPlateGate(); // catch a plate added before the bus was ready
            return;
          }
        } catch (e) { /* ignore */ }
        if (tries < 150) setTimeout(function () { attachBus(tries + 1); }, 100); // up to ~15s
      })(0);
      syncPlateGate(); // correct initial state (hidden unless a plate is pre-selected)
    }

    // Flag the red required state as soon as a blocked add is attempted. Capture
    // phase so we still see the click even though the AGB handler returns early.
    // For the plate gate, only while it is actually active (a plate is present).
    ADD_BUTTON_IDS.forEach(function (id) {
      var btn = document.getElementById(id);
      if (!btn) return;
      btn.addEventListener('click', function () {
        // Capture phase runs BEFORE the AGB bubble-phase handleAddAllClick (which
        // reads value === '✓'), so re-derive value from the authoritative `checked`
        // here FIRST — guarantees a visibly-checked box is honoured even if the
        // browser restored it without ever firing a 'change' event.
        syncValue();
        if (plateConditional && !hasPlateInLoadout()) return; // gate inactive → no armor → don't block
        if (!checkbox.checked) markInvalid();
      }, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
